
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useFormulas } from '@/hooks/useFormulas';
import { FormulaCard } from './FormulaCard';
import { FormulaDetailDialog } from './FormulaDetailDialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Formula } from '@/types/formula';

interface FormulasListProps {
  portfolioId: string;
  onAddClick: () => void;
}

export const FormulasList = ({ portfolioId, onAddClick }: FormulasListProps) => {
  const { data: formulas, isLoading } = useFormulas(portfolioId);
  const [selectedFormula, setSelectedFormula] = useState<Formula | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  const handleViewDetails = (formula: Formula) => {
    setSelectedFormula(formula);
    setIsDetailDialogOpen(true);
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>Loading formulas...</p>
      </div>
    );
  }
  
  if (!formulas || formulas.length === 0) {
    return (
      <EmptyState 
        icon={<Info className="h-10 w-10 text-primary" />}
        title="No Formulas Yet"
        description="Formulas represent chemical compositions of your products. Define your first formula to get started."
        action={
          <Button onClick={onAddClick} className="mt-4">
            <Plus className="mr-2 h-4 w-4" />
            Create Formula
          </Button>
        }
      />
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Formulas</h3>
        <Button onClick={onAddClick} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Formula
        </Button>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {formulas.map((formula) => (
          <FormulaCard 
            key={formula.id} 
            formula={formula}
            onViewDetails={handleViewDetails}
          />
        ))}
      </div>

      <FormulaDetailDialog
        formula={selectedFormula}
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
      />
    </div>
  );
};
